<script setup>
import { watch, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'

const { t } = useI18n()
const { setting } = useCurrency()

const props = defineProps({
  open:   { type: Boolean, required: true },
  damage: { type: Object,  default: null },
})
const emit = defineEmits(['close'])

const form = useForm({ repair_cost_cents: 0, repair_notes: '' })

watch(() => props.open, (o) => {
  if (o && props.damage) {
    form.repair_cost_cents = props.damage.repair_cost_cents || 0
    form.repair_notes = ''
    form.clearErrors()
  }
})

const repairDollars = computed({
  get: () => (form.repair_cost_cents / 100).toFixed(2),
  set: (v) => { form.repair_cost_cents = Math.round(parseFloat(v) * 100) || 0 }
})

const submit = () => {
  form.transform(d => ({ damage: d })).patch(`/damages/${props.damage.id}/mark_repaired`, {
    onSuccess: () => emit('close')
  })
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60" @click.self="$emit('close')">
      <div class="w-full h-full md:h-auto md:max-w-md md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
        <header class="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[color:var(--line)]">
          <h2 class="text-base fw-value text-[color:var(--ink)]">{{ t('damage.modal.markRepairedTitle') }}</h2>
          <button class="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]" type="button" aria-label="Close" @click="$emit('close')">
            <Icon name="x" :size="18" />
          </button>
        </header>

        <form id="mark-repaired-form" class="flex-1 overflow-y-auto px-4 py-4 space-y-4" @submit.prevent="submit">
          <div>
            <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.repairCost') }} ({{ setting.currency_code }})</label>
            <UiInput v-model="repairDollars" type="number" step="0.01" :error="form.errors.repair_cost_cents" />
          </div>
          <div>
            <label class="block text-sm fw-value mb-1.5 text-[color:var(--ink)]">{{ t('damage.fields.repairNotes') }}</label>
            <UiTextarea v-model="form.repair_notes" :rows="3" :error="form.errors.repair_notes" />
          </div>
        </form>

        <footer class="flex-shrink-0 px-4 py-3 border-t border-[color:var(--line)] flex items-center justify-end gap-2 pb-[max(env(safe-area-inset-bottom),0.75rem)] md:pb-3">
          <UiButton variant="ghost" type="button" @click="$emit('close')">{{ t('damage.cancel') }}</UiButton>
          <UiButton variant="primary" type="button" :loading="form.processing" @click="submit">
            {{ t('damage.markRepaired') }}
          </UiButton>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
